import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../../../services/api';
import { useAuthContext } from '../../../../context/AuthContext';
import './UsersView.css';
import UserActions from './UserActions/UserActions';

// ── Supabase storage resolver for ID documents ────────────────────────────────
// The backend stores id_front / id_back as relative Supabase paths like
//   id_documents/2026/04/22/file.jpg
// resolveImageUrl() would incorrectly prepend the Railway host. We need the
// Supabase public storage base URL instead.
const SUPABASE_STORAGE_BASE =
  'https://vngtcztqywifguvpszyx.supabase.co/storage/v1/object/public/media';

function resolveIdDocumentUrl(path) {
  if (!path || typeof path !== 'string') return null;

  // ── Strip double-prefixed Supabase URLs ───────────────────────────────────
  // The backend sometimes double-wraps URLs, storing values like:
  //   https://…supabase.co/…/media/https%3A/…supabase.co/…/media/id_documents/…
  // The real URL starts at the LAST occurrence of "https" in the string.
  const lastHttpsIdx = path.lastIndexOf('https');
  if (lastHttpsIdx > 0) {
    // There's a second "https" — extract from there and decode any %3A etc.
    let cleanPath = path.slice(lastHttpsIdx);
    try { cleanPath = decodeURIComponent(cleanPath); } catch {}
    // Normalise https:/ → https:// (only one slash survives encoding sometimes)
    cleanPath = cleanPath.replace(/^https?:\/{1}(?!\/)/, m => m + '/');
    return cleanPath;
  }

  // ── Already a clean full URL ──────────────────────────────────────────────
  if (path.startsWith('http://') || path.startsWith('https://')) return path;

  // ── Relative Supabase path — prepend the bucket base ─────────────────────
  const slash = path.startsWith('/') ? '' : '/';
  return `${SUPABASE_STORAGE_BASE}${slash}${path}`;
}
// ─────────────────────────────────────────────────────────────────────────────

// =============================================================================
// SNAPSHOT HELPERS — stored in the backend (UserProfile.verified_snapshot).
// Saved by admin_set_user_verification when verdict == 'verified'.
// Loaded directly from user.verified_snapshot in the API response.
// localStorage fallback included for users verified before this migration.
// =============================================================================

function loadSnapshot(userId, userObj) {
  // ✅ Primary: read from backend field (persists across browsers/devices)
  if (userObj?.verified_snapshot) return userObj.verified_snapshot;
  // ✅ Fallback: read from localStorage (pre-migration snapshots)
  try {
    const raw = localStorage.getItem(`verified_snapshot_${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// =============================================================================
// USER MANAGE MODAL
// =============================================================================
function UserManageModal({ user, onClose, onSaveEdit, onSaveVerification, getPhoneNumber, getLocation }) {
  const [form, setForm] = useState({
    first_name:        user.first_name || '',
    last_name:         user.last_name  || '',
    email:             user.email      || '',
    phone:             getPhoneNumber(user) || '',
    username:          user.username   || '',
    is_active:         user.is_active !== undefined ? user.is_active : true,
    referral_allowed:  user.referral_allowed || false,
    referral_mpesa:    user.referral_mpesa   || '',
  });
  const [saving,     setSaving]     = useState(false);
  const [saveMsg,    setSaveMsg]    = useState('');
  const [savingVerd, setSavingVerd] = useState(false);
  const [verdMsg,    setVerdMsg]    = useState('');

  // ── Load snapshot: backend field first, localStorage fallback ───────────────
  const snap = loadSnapshot(user.id, user);

  // ── Compute which fields changed vs snapshot ───────────────────────────────
  const norm = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');

  const currentForDiff = {
    first_name:    form.first_name           || '',
    last_name:     form.last_name            || '',
    email:         form.email                || '',
    phone:         form.phone                || user.phone_number || user.phone || '',
    location:      user.location             || user.city || user.town || '',
    username:      form.username             || user.username || '',
    business_name: user.business_name        || '',
    has_id_front:  !!user.id_front,
    has_id_back:   !!user.id_back,
  };

  const changedFields = new Set();
  if (snap) {
    Object.entries(currentForDiff).forEach(([k, v]) => {
      const sv = snap[k];
      if (typeof v === 'boolean') {
        const snapBool = sv === true || sv === 'true';
        if (v !== snapBool) changedFields.add(k);
      } else {
        if (norm(v) !== norm(sv)) changedFields.add(k);
      }
    });
    if (process.env.NODE_ENV !== 'production') {
      console.group(`🔍 [UsersView diff] user=${user.id} | changedFields=${changedFields.size}`);
      Object.entries(currentForDiff).forEach(([k, v]) => {
        const sv = snap[k];
        const changed = changedFields.has(k);
        console.log(`  ${changed ? '✏️ CHANGED' : '  same   '} ${k}: snap="${sv}" → current="${v}"`);
      });
      console.groupEnd();
    }
  }

  const handleSaveProfile = async () => {
    setSaving(true); setSaveMsg('');
    try {
      await onSaveEdit(user.id, form);
      setSaveMsg('✅ Profile saved');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch { setSaveMsg('❌ Failed to save'); }
    finally { setSaving(false); }
  };

  // ✅ Saves referral_allowed immediately on toggle — no Save Profile needed.
  // Updates table row instantly via the optimistic update in handleSaveEdit.
  const [referralSaving, setReferralSaving] = useState(false);
  const [referralMsg,    setReferralMsg]    = useState('');

  const handleReferralToggle = async (checked) => {
    setForm(prev => ({ ...prev, referral_allowed: checked }));
    setReferralSaving(true);
    setReferralMsg('');
    try {
      await onSaveEdit(user.id, { referral_allowed: checked });
      setReferralMsg(checked ? '✅ Referrals enabled' : '✅ Disabled');
      setTimeout(() => setReferralMsg(''), 2500);
    } catch {
      // Revert the checkbox if the API call failed
      setForm(prev => ({ ...prev, referral_allowed: !checked }));
      setReferralMsg('❌ Failed');
      setTimeout(() => setReferralMsg(''), 2500);
    } finally {
      setReferralSaving(false);
    }
  };

  const location = getLocation(user);

  const checks = [
    { label: 'First Name', ok: !!(form.first_name?.trim()) },
    { label: 'Last Name',  ok: !!(form.last_name?.trim())  },
    { label: 'Email',      ok: !!(form.email?.trim())       },
    { label: 'Phone',      ok: !!(form.phone?.trim())       },
    { label: 'Location',   ok: !!location                   },
    { label: 'Username',   ok: !!(form.username?.trim())    },
    { label: 'ID Front',   ok: !!user.id_front              },
    { label: 'ID Back',    ok: !!user.id_back               },
  ];
  const completePct = Math.round((checks.filter(c => c.ok).length / checks.length) * 100);

  const [idFront, setIdFront] = useState(resolveIdDocumentUrl(user.id_front) || null);
  const [idBack,  setIdBack]  = useState(resolveIdDocumentUrl(user.id_back)  || null);
  const frontRef = useRef(); const backRef = useRef();
  const [zoomImg, setZoomImg] = useState(null); // URL of image to zoom, or null
  const [uploadingId, setUploadingId] = useState(null); // 'front' | 'back' | null
  const [uploadMsg,   setUploadMsg]   = useState('');

  // ── Upload a new ID document on behalf of the user (replaces existing) ──
  const handleIdUpload = async (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview immediately
    const reader = new FileReader();
    reader.onload = ev => {
      if (side === 'front') setIdFront(ev.target.result);
      else                  setIdBack(ev.target.result);
    };
    reader.readAsDataURL(file);

    setUploadingId(side);
    setUploadMsg('');
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token') || '';
      const fd = new FormData();
      fd.append(side === 'front' ? 'id_front' : 'id_back', file);
      const res = await fetch(
        `${(process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api')}/admin/users/${user.id}/`,
        { method: 'PATCH', headers: { Authorization: `Token ${token}` }, body: fd }
      );
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      const newUrl = resolveIdDocumentUrl(side === 'front' ? data.id_front : data.id_back) || null;
      if (newUrl) {
        if (side === 'front') setIdFront(newUrl);
        else                  setIdBack(newUrl);
      }
      setUploadMsg(`✅ ID ${side} replaced successfully`);
      setTimeout(() => setUploadMsg(''), 3000);
    } catch (err) {
      setUploadMsg(`❌ ${err.message || 'Upload failed'}`);
      setTimeout(() => setUploadMsg(''), 4000);
    } finally {
      setUploadingId(null);
      // reset file input so same file can be re-selected
      if (side === 'front' && frontRef.current) frontRef.current.value = '';
      if (side === 'back'  && backRef.current)  backRef.current.value  = '';
    }
  };

  const normaliseStatus = (s) => s === 'reverify' ? 'pending_reverification' : (s || 'unverified');

  const deriveInitial = () => {
    const s = normaliseStatus(user.verification_status);
    if (s !== 'unverified') return s;
    if (user.email_verified || user.is_email_verified)
      return (user.phone_verified || user.is_phone_verified) ? 'verified' : 'partial';
    return 'unverified';
  };
  const [verdict, setVerdict] = useState(deriveInitial());

  const VERDICTS = [
    { value: 'verified',               label: '✅ Verified',        bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
    { value: 'partial',                label: '~ Partial',           bg: '#fffbeb', color: '#d97706', border: '#fcd34d' },
    { value: 'pending',                label: '⏳ Under Review',     bg: '#eff6ff', color: '#2563eb', border: '#93c5fd' },
    { value: 'pending_reverification', label: '🔄 Re-verification',  bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
    { value: 'unverified',             label: '✗ Not Verified',      bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    { value: 'rejected',               label: '🚫 Rejected',         bg: '#fdf2f8', color: '#9333ea', border: '#d8b4fe' },
  ];

  const inputStyle = (fieldKey) => ({
    width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
    border: changedFields.has(fieldKey) ? '2px solid #f59e0b' : '1.5px solid #e5e7eb',
    outline: 'none', boxSizing: 'border-box',
    background: changedFields.has(fieldKey) ? '#fffbeb' : '#fff',
    color: '#111', transition: 'border-color .15s',
    fontWeight: changedFields.has(fieldKey) ? 700 : 400,
  });

  const labelStyle = (fieldKey) => ({
    fontSize: 12,
    fontWeight: changedFields.has(fieldKey) ? 800 : 600,
    color: changedFields.has(fieldKey) ? '#d97706' : '#6b7280',
    marginBottom: 4,
    display: 'block',
  });

  const initials = user.first_name && user.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : (user.username || 'U').substring(0, 2).toUpperCase();

  const displayName = user.first_name || user.last_name
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
    : user.username;

  const submittedAt = user.verification_submitted_at
    ? new Date(user.verification_submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const normStatus = normaliseStatus(user.verification_status);
  const [showFullModal, setShowFullModal] = useState(normStatus !== 'verified');

  const DiffBanner = () => {
    if (normStatus !== 'pending_reverification') return null;

    const FIELD_LABELS = {
      first_name: 'First Name', last_name: 'Last Name', email: 'Email',
      phone: 'Phone', location: 'Location', username: 'Username',
      business_name: 'Business Name', has_id_front: 'ID Front', has_id_back: 'ID Back',
    };

    const getDisplayVal = (key, fromSnap) => {
      const val = fromSnap ? snap?.[key] : currentForDiff[key];
      if (key === 'has_id_front' || key === 'has_id_back')
        return (val === true || val === 'true') ? '✅ Uploaded' : '✗ Missing';
      return String(val || '').trim() || '—';
    };

    return (
      <div style={{ background: '#fff7ed', border: '1.5px solid #fdba74', borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: changedFields.size ? 10 : 0 }}>
          <span style={{ fontSize: 18 }}>🔄</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#9a3412' }}>
              {changedFields.size > 0 ? 'Profile Updated After Verification' : 'Re-verification Requested'}
              {changedFields.size > 0 && (
                <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#fed7aa', color: '#9a3412' }}>
                  {changedFields.size} field{changedFields.size > 1 ? 's' : ''} changed
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#c2410c', marginTop: 2 }}>
              {snap
                ? changedFields.size > 0
                  ? `Verified on ${snap.verified_at ? new Date(snap.verified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}. Changed fields are highlighted in amber below.`
                  : `The user re-submitted for review. Verified on ${snap.verified_at ? new Date(snap.verified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} — no profile field changes detected since then.`
                : 'The user submitted a re-verification request. No previous snapshot available to compare against.'}
            </div>
          </div>
        </div>

        {snap && changedFields.size > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>
              Changed Fields
            </div>
            {[...changedFields].map(key => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#fff', borderRadius: 7, border: '1px solid #fed7aa', fontSize: 12 }}>
                <span style={{ fontWeight: 800, color: '#9a3412', minWidth: 96, flexShrink: 0 }}>{FIELD_LABELS[key] || key}</span>
                <span style={{ color: '#9ca3af', textDecoration: 'line-through', flex: 1, fontStyle: 'italic', fontSize: 12 }}>{getDisplayVal(key, true)}</span>
                <span style={{ color: '#9ca3af', flexShrink: 0 }}>→</span>
                <span style={{ color: '#b45309', fontWeight: 800, flex: 1, fontSize: 12, background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>{getDisplayVal(key, false)}</span>
              </div>
            ))}
          </div>
        )}

        {!snap && (
          <div style={{ fontSize: 12, color: '#92400e', marginTop: 8, fontStyle: 'italic' }}>
            ℹ️ Snapshot not available — verify the user once to enable future change tracking.
          </div>
        )}
      </div>
    );
  };

  // ── Collapsed view for already-verified users ─────────────────────────────
  if (!showFullModal) {
    return (
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 24px 80px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>{initials}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#111' }}>{displayName}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>@{user.username}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700, background: '#f0fdf4', color: '#16a34a', border: '1.5px solid #86efac' }}>✅ Verified</span>
              <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#f1f5f9', cursor: 'pointer', fontSize: 17, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
          </div>
          <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', borderBottom: '1px solid #bbf7d0', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(22,163,74,0.15)' }}>
              <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
                <circle cx='12' cy='12' r='11' fill='#dcfce7' stroke='#16a34a' strokeWidth='1.5'/>
                <path d='M7 12.5l3.5 3.5 6.5-7' stroke='#16a34a' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#15803d' }}>Account Verified</div>
              <div style={{ fontSize: 12, color: '#166534', marginTop: 3 }}>This account has been verified. You can re-verify if details have changed.</div>
            </div>
          </div>
          <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid #e5e7eb', borderRadius: 8, margin: '16px 24px', overflow: 'hidden' }}>
            {[
              { label: 'Name',     value: displayName },
              { label: 'Email',    value: user.email || '—' },
              { label: 'Phone',    value: getPhoneNumber(user) || '—' },
              { label: 'Location', value: getLocation(user) || '—' },
              { label: 'ID Docs',  value: user.id_front && user.id_back ? '✅ Front & Back uploaded' : '⚠️ Incomplete' },
            ].map(({ label, value }, idx, arr) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', fontSize: 13, borderBottom: idx < arr.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 80 }}>{label}</span>
                <span style={{ fontWeight: 600, color: label === 'ID Docs' && user.id_front && user.id_back ? '#16a34a' : '#111' }}>{value}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, padding: '0 24px 20px' }}>
            <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 10, cursor: 'pointer', border: '1.5px solid #d1d5db', background: '#fff', fontSize: 14, fontWeight: 600, color: '#374151' }}>Close</button>
            <button onClick={() => setShowFullModal(true)} style={{ flex: 2, padding: '10px 0', borderRadius: 10, cursor: 'pointer', border: 'none', fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>✎ Re-verify / Edit Status</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', flexShrink: 0, position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>{initials}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#111' }}>{displayName}</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>@{user.username}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {(() => {
              const v = VERDICTS.find(x => x.value === normStatus) || VERDICTS[4];
              return <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700, background: v.bg, color: v.color, border: `1.5px solid ${v.border}` }}>{v.label}</span>;
            })()}
            {normStatus === 'pending_reverification' && (
              <span style={{ padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: changedFields.size > 0 ? '#fef3c7' : '#eff6ff', color: changedFields.size > 0 ? '#92400e' : '#2563eb', border: `1.5px solid ${changedFields.size > 0 ? '#fde047' : '#93c5fd'}` }}>
                {changedFields.size > 0 ? `✏️ ${changedFields.size} changed` : '🔄 Re-submitted'}
              </span>
            )}
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#f1f5f9', cursor: 'pointer', fontSize: 17, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

          {normStatus === 'pending' && (
            <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ fontSize: 18 }}>⏳</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1d4ed8' }}>Verification Request Pending</div>
                <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>
                  This user submitted their profile for verification{submittedAt ? ` on ${submittedAt}` : ''}. Review their details and ID below, then set a status.
                </div>
              </div>
            </div>
          )}

          <DiffBanner />

          {changedFields.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12 }}>
              <span style={{ fontSize: 15 }}>✏️</span>
              <span style={{ color: '#92400e', fontWeight: 600 }}>Fields highlighted in amber were changed since the last verification. The old value is shown above; the current value is in the input below.</span>
            </div>
          )}
          {normStatus === 'pending_reverification' && snap && changedFields.size === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, fontSize: 12 }}>
              <span style={{ fontSize: 15 }}>ℹ️</span>
              <span style={{ color: '#1d4ed8', fontWeight: 600 }}>The user re-submitted but their current profile matches the verified snapshot — no field changes detected. Review their ID documents below before deciding.</span>
            </div>
          )}

          {/* Profile completeness */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Profile Completeness</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: completePct === 100 ? '#16a34a' : completePct >= 50 ? '#d97706' : '#dc2626' }}>{completePct}%</span>
            </div>
            <div style={{ height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ height: '100%', borderRadius: 99, transition: 'width .4s', width: `${completePct}%`, background: completePct === 100 ? '#16a34a' : completePct >= 50 ? '#f59e0b' : '#ef4444' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
              {checks.map(c => (
                <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: c.ok ? '#16a34a' : '#dc2626' }}>
                  <span>{c.ok ? '✅' : '✗'}</span>
                  <span style={{ fontWeight: 600 }}>{c.label}</span>
                  {!c.ok && <span style={{ fontSize: 11, color: '#9ca3af' }}>(missing)</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Profile fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>
              Profile Details
              {changedFields.size > 0 && <span style={{ marginLeft: 8, fontWeight: 700, color: '#d97706', textTransform: 'none', fontSize: 11 }}>— amber fields changed since last verification</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle('first_name')}>First Name {changedFields.has('first_name') && <span style={{ marginLeft: 4 }}>✏️</span>}</label>
                {changedFields.has('first_name') && snap && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, fontStyle: 'italic' }}>Was: <span style={{ textDecoration: 'line-through', color: '#d97706' }}>{snap.first_name || '—'}</span></div>}
                <input style={inputStyle('first_name')} type="text" value={form.first_name}
                  onChange={e => setForm({ ...form, first_name: e.target.value })}
                  onFocus={e => { if (!changedFields.has('first_name')) e.target.style.borderColor = '#6366f1'; }}
                  onBlur={e => { if (!changedFields.has('first_name')) e.target.style.borderColor = '#e5e7eb'; }} />
              </div>
              <div>
                <label style={labelStyle('last_name')}>Last Name {changedFields.has('last_name') && <span style={{ marginLeft: 4 }}>✏️</span>}</label>
                {changedFields.has('last_name') && snap && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, fontStyle: 'italic' }}>Was: <span style={{ textDecoration: 'line-through', color: '#d97706' }}>{snap.last_name || '—'}</span></div>}
                <input style={inputStyle('last_name')} type="text" value={form.last_name}
                  onChange={e => setForm({ ...form, last_name: e.target.value })}
                  onFocus={e => { if (!changedFields.has('last_name')) e.target.style.borderColor = '#6366f1'; }}
                  onBlur={e => { if (!changedFields.has('last_name')) e.target.style.borderColor = '#e5e7eb'; }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle('email')}>Email {changedFields.has('email') && <span style={{ marginLeft: 4 }}>✏️</span>}</label>
                {changedFields.has('email') && snap && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, fontStyle: 'italic' }}>Was: <span style={{ textDecoration: 'line-through', color: '#d97706' }}>{snap.email || '—'}</span></div>}
                <input style={inputStyle('email')} type="email" value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  onFocus={e => { if (!changedFields.has('email')) e.target.style.borderColor = '#6366f1'; }}
                  onBlur={e => { if (!changedFields.has('email')) e.target.style.borderColor = '#e5e7eb'; }} />
              </div>
              <div>
                <label style={labelStyle('phone')}>Phone {changedFields.has('phone') && <span style={{ marginLeft: 4 }}>✏️</span>}</label>
                {changedFields.has('phone') && snap && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, fontStyle: 'italic' }}>Was: <span style={{ textDecoration: 'line-through', color: '#d97706' }}>{snap.phone || '—'}</span></div>}
                <input style={inputStyle('phone')} type="tel" value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  onFocus={e => { if (!changedFields.has('phone')) e.target.style.borderColor = '#6366f1'; }}
                  onBlur={e => { if (!changedFields.has('phone')) e.target.style.borderColor = '#e5e7eb'; }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'block' }}>Username</label>
                <input style={{ ...inputStyle('username'), background: '#f8fafc', color: '#9ca3af', cursor: 'not-allowed' }} type="text" value={form.username} disabled />
              </div>
              <div>
                <label style={labelStyle('location')}>Location {changedFields.has('location') && <span style={{ marginLeft: 4 }}>✏️</span>}</label>
                {changedFields.has('location') && snap && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, fontStyle: 'italic' }}>Was: <span style={{ textDecoration: 'line-through', color: '#d97706' }}>{snap.location || '—'}</span></div>}
                <input style={{ ...inputStyle('location'), cursor: 'default' }} type="text" value={location || user.location || '—'} readOnly title="Set by the user in their profile" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Joined</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111', marginTop: 2 }}>
                  {user.date_joined ? new Date(user.date_joined).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </div>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Business Name</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: changedFields.has('business_name') ? '#d97706' : '#111', marginTop: 2 }}>
                  {changedFields.has('business_name') && <span style={{ marginRight: 4 }}>✏️</span>}
                  {user.business_name || '—'}
                  {changedFields.has('business_name') && snap && (
                    <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginTop: 2 }}>Was: <span style={{ textDecoration: 'line-through' }}>{snap.business_name || '—'}</span></div>
                  )}
                </div>
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} style={{ width: 16, height: 16, accentColor: '#6366f1' }} />
              Active User
            </label>

            {/* ── Allow User For Referrals ── */}
            <div style={{ background: form.referral_allowed ? '#ecfdf5' : '#f8fafc', border: `1.5px solid ${form.referral_allowed ? '#6ee7b7' : '#e2e8f0'}`, borderRadius: 10, padding: '12px 14px', transition: 'all 0.2s' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: referralSaving ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.referral_allowed}
                  disabled={referralSaving}
                  onChange={e => handleReferralToggle(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#059669', flexShrink: 0, cursor: referralSaving ? 'not-allowed' : 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: form.referral_allowed ? '#059669' : '#374151' }}>
                    {form.referral_allowed ? '✅ Allowed to Refer' : '🔗 Allow User For Referrals'}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {form.referral_allowed
                      ? 'This user can earn commissions by referring new users.'
                      : 'Enable to let this user participate in the referral programme.'}
                  </div>
                </div>
                {referralSaving && (
                  <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap' }}>Saving…</span>
                )}
                {!referralSaving && referralMsg && (
                  <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', color: referralMsg.startsWith('✅') ? '#059669' : '#dc2626' }}>{referralMsg}</span>
                )}
              </label>
            </div>
            {/* ── Referral M-Pesa number ── */}
            <div style={{ background: form.referral_mpesa ? '#f0fdf4' : '#f8fafc', border: `1.5px solid ${form.referral_mpesa ? '#86efac' : '#e2e8f0'}`, borderRadius: 10, padding: '12px 14px', transition: 'all 0.2s' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: form.referral_mpesa ? '#15803d' : '#6b7280', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                📱 Referral M-Pesa
              </label>
              <input
                type="tel"
                value={form.referral_mpesa}
                onChange={e => setForm(prev => ({ ...prev, referral_mpesa: e.target.value }))}
                placeholder="254XXXXXXXXX"
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
                  border: form.referral_mpesa ? '1.5px solid #86efac' : '1.5px solid #e5e7eb',
                  outline: 'none', boxSizing: 'border-box',
                  background: form.referral_mpesa ? '#f0fdf4' : '#fff',
                  color: '#111', fontFamily: 'monospace', fontWeight: form.referral_mpesa ? 700 : 400,
                  transition: 'border-color .15s',
                }}
                onFocus={e => { e.target.style.borderColor = '#16a34a'; }}
                onBlur={e => { e.target.style.borderColor = form.referral_mpesa ? '#86efac' : '#e5e7eb'; }}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>
                {form.referral_mpesa
                  ? 'M-Pesa cash-out number provided by the user for referral payouts.'
                  : 'User has not yet provided a referral M-Pesa number.'}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={handleSaveProfile} disabled={saving} style={{ padding: '8px 20px', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', border: 'none', background: saving ? '#a5b4fc' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', fontSize: 13, fontWeight: 700, color: '#fff' }}>
                {saving ? 'Saving…' : '💾 Save Profile'}
              </button>
              {saveMsg && <span style={{ fontSize: 12, fontWeight: 600, color: saveMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{saveMsg}</span>}
            </div>
          </div>

          {/* ID Documents */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.5px' }}>ID Documents</div>
            {uploadMsg && (
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, padding: '7px 12px', borderRadius: 8, background: uploadMsg.startsWith('✅') ? '#f0fdf4' : '#fef2f2', color: uploadMsg.startsWith('✅') ? '#16a34a' : '#dc2626', border: `1px solid ${uploadMsg.startsWith('✅') ? '#bbf7d0' : '#fca5a5'}` }}>
                {uploadMsg}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { label: 'ID Front', value: idFront, side: 'front', ref: frontRef },
                { label: 'ID Back',  value: idBack,  side: 'back',  ref: backRef  },
              ].map(({ label, value, side, ref }) => {
                const docChanged = changedFields.has(side === 'front' ? 'has_id_front' : 'has_id_back');
                const isUploading = uploadingId === side;
                return (
                  <div key={label}>
                    <div style={{ fontSize: 12, fontWeight: docChanged ? 800 : 600, color: docChanged ? '#d97706' : '#374151', marginBottom: 6 }}>
                      {label} {docChanged && <span>✏️ Changed</span>}
                    </div>
                    {/* Image preview — click to zoom */}
                    <div
                      onClick={() => value && !value.startsWith('data:') && setZoomImg(value)}
                      style={{ height: 130, borderRadius: 10, border: `2px solid ${docChanged ? '#f59e0b' : value ? '#6366f1' : '#d1d5db'}`, background: value ? '#000' : '#f9fafb', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: value && !value.startsWith('data:') ? 'zoom-in' : 'default', position: 'relative' }}>
                      {value ? (
                        <>
                          <img
                            src={value}
                            alt={label}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              // If resolved URL fails, show broken placeholder
                              e.currentTarget.style.opacity = '0.4';
                            }}
                          />
                          {!value.startsWith('data:') && (
                            <div style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: 700, pointerEvents: 'none' }}>🔍 Click to zoom</div>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', color: '#9ca3af' }}><div style={{ fontSize: 26, marginBottom: 4 }}>🪪</div><div style={{ fontSize: 11, fontWeight: 600 }}>Not uploaded</div></div>
                      )}
                    </div>
                    {/* Status + re-upload button */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                      <div style={{ fontSize: 11, color: docChanged ? '#d97706' : value ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>
                        {value ? (docChanged ? '✏️ Newly uploaded' : '✅ Uploaded') : '— Not uploaded'}
                      </div>
                      {/* Re-upload / Upload button */}
                      <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        cursor: isUploading ? 'not-allowed' : 'pointer',
                        background: isUploading ? '#e0e7ff' : value ? '#fff7ed' : '#eff6ff',
                        border: `1.5px solid ${isUploading ? '#c7d2fe' : value ? '#fbbf24' : '#93c5fd'}`,
                        color: isUploading ? '#6366f1' : value ? '#d97706' : '#2563eb',
                        opacity: isUploading ? 0.7 : 1,
                        transition: 'all .15s',
                      }}>
                        {isUploading ? '⏳ Uploading…' : value ? '🔄 Replace' : '⬆ Upload'}
                        <input
                          type="file"
                          accept="image/*"
                          ref={ref}
                          style={{ display: 'none' }}
                          disabled={isUploading}
                          onChange={(e) => handleIdUpload(e, side)}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Assign Verification Status */}
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: '18px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.5px' }}>Assign Verification Status</div>
            {/* ── Rejection warning banner — shown when 'rejected' is selected ── */}
            {verdict === 'rejected' && (
              <div style={{ background: '#fdf2f8', border: '1.5px solid #d8b4fe', borderRadius: 10, padding: '12px 14px', marginBottom: 12, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#7e22ce', marginBottom: 4 }}>🚫 Rejection will restore account to last verified state</div>
                {snap ? (
                  <div style={{ color: '#6b21a8', lineHeight: 1.6 }}>
                    Profile will revert to: <strong>{[snap.first_name, snap.last_name].filter(Boolean).join(' ') || '—'}</strong>
                    {snap.email    && <> · {snap.email}</>}
                    {snap.phone    && <> · {snap.phone}</>}
                    {snap.location && <> · {snap.location}</>}
                    <br/>Status will be restored to <strong>✅ Verified</strong>.
                  </div>
                ) : (
                  <div style={{ color: '#6b21a8' }}>No snapshot found — rejecting will mark the account as <strong>Unverified</strong>.</div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {VERDICTS.map(v => (
                <button key={v.value} onClick={() => setVerdict(v.value)} style={{
                  flex: '1 1 calc(33% - 8px)', padding: '10px 6px', borderRadius: 10, cursor: 'pointer',
                  border: `2px solid ${verdict === v.value ? v.border : '#e5e7eb'}`,
                  background: verdict === v.value ? v.bg : '#fff',
                  color: verdict === v.value ? v.color : '#6b7280',
                  fontSize: 12, fontWeight: 700, transition: 'all .15s',
                  boxShadow: verdict === v.value ? `0 0 0 3px ${v.border}55` : 'none',
                }}>{v.label}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 14 }}>
              {completePct === 100 && idFront && idBack
                ? '✅ All fields and ID documents present — ready to verify.'
                : `ℹ️ Profile ${completePct}% complete${!idFront || !idBack ? ', ID documents missing' : ''}.`}
            </div>
            {verdMsg && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: verdMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{verdMsg}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 10, cursor: 'pointer', border: '1.5px solid #d1d5db', background: '#fff', fontSize: 14, fontWeight: 600, color: '#374151' }}>Cancel</button>
              <button
                disabled={savingVerd}
                onClick={async () => {
                  setSavingVerd(true); setVerdMsg('');
                  try {
                    await onSaveVerification(user.id, verdict, user);
                    setVerdMsg(`✅ ${verdict === 'rejected' ? 'Changes rejected — profile restored' : `Status set to "${verdict}"`}`);
                    setTimeout(() => { setVerdMsg(''); onClose(); }, 1400);
                  } catch (e) {
                    setVerdMsg(`❌ ${e?.message || 'Failed to save'}`);
                  } finally { setSavingVerd(false); }
                }}
                style={{
                  flex: 2, padding: '10px 0', borderRadius: 10,
                  cursor: savingVerd ? 'not-allowed' : 'pointer',
                  border: 'none', fontSize: 14, fontWeight: 700, color: '#fff',
                  background: savingVerd ? '#a5b4fc'
                    : verdict === 'verified'               ? 'linear-gradient(135deg,#16a34a,#22c55e)'
                    : verdict === 'partial'                ? 'linear-gradient(135deg,#d97706,#f59e0b)'
                    : verdict === 'pending'                ? 'linear-gradient(135deg,#2563eb,#3b82f6)'
                    : verdict === 'pending_reverification' ? 'linear-gradient(135deg,#ea580c,#f97316)'
                    : verdict === 'rejected'               ? 'linear-gradient(135deg,#9333ea,#a855f7)'
                    :                                       'linear-gradient(135deg,#dc2626,#ef4444)',
                }}>
                {savingVerd ? 'Saving…'
                  : verdict === 'verified'               ? '✅ Mark as Verified'
                  : verdict === 'partial'                ? '~ Mark as Partial'
                  : verdict === 'pending'                ? '⏳ Mark Under Review'
                  : verdict === 'pending_reverification' ? '🔄 Mark for Re-verification'
                  : verdict === 'rejected'               ? '🚫 Reject & Restore Previous'
                  :                                       '✗ Mark as Unverified'}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ── ID Image Lightbox ── */}
      {zoomImg && (
        <div
          onClick={() => setZoomImg(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 20000,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, cursor: 'zoom-out',
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <img
              src={zoomImg}
              alt="ID Document"
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', display: 'block' }}
            />
            <button
              onClick={() => setZoomImg(null)}
              style={{
                position: 'absolute', top: -14, right: -14,
                width: 32, height: 32, borderRadius: '50%',
                background: '#fff', border: 'none', cursor: 'pointer',
                fontSize: 18, color: '#374151', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >×</button>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>Click anywhere outside to close</span>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// STAR RATING DISPLAY
// =============================================================================
function StarRating({ rating, count }) {
  if (!rating && !count) return <span style={{ fontSize: 12, color: '#d1d5db' }}>— No reviews</span>;
  const full  = Math.floor(rating);
  const half  = rating - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {[...Array(full)].map((_, i) => (
          <svg key={`f${i}`} width="13" height="13" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        ))}
        {half && (
          <svg key="h" width="13" height="13" viewBox="0 0 24 24" stroke="#f59e0b" strokeWidth="1" style={{ overflow: 'visible' }}>
            <defs><linearGradient id="half-uv"><stop offset="50%" stopColor="#f59e0b"/><stop offset="50%" stopColor="none"/></linearGradient></defs>
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="url(#half-uv)"/>
          </svg>
        )}
        {[...Array(empty)].map((_, i) => (
          <svg key={`e${i}`} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        ))}
        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginLeft: 2 }}>{rating > 0 ? rating.toFixed(1) : '—'}</span>
      </div>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>{count === 1 ? '1 review' : `${count} reviews`}</span>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const UsersView = () => {
  const { getToken } = useAuthContext();
  const [users,        setUsers]        = useState([]);
  const [ads,          setAds]          = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [roleFilter,   setRoleFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUsers,setSelectedUsers]= useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(() => {
    try { const s = parseInt(localStorage.getItem('uv_usersPerPage'), 10); return (!isNaN(s) && s >= 1 && s <= 200) ? s : 20; }
    catch { return 20; }
  });
  const [pageSizeInput, setPageSizeInput] = useState(() => {
    try { const s = parseInt(localStorage.getItem('uv_usersPerPage'), 10); return (!isNaN(s) && s >= 1 && s <= 200) ? String(s) : '20'; }
    catch { return '20'; }
  });
  const handlePageSizeChange = () => {
    const size = parseInt(pageSizeInput, 10);
    if (!isNaN(size) && size >= 1 && size <= 200) {
      setUsersPerPage(size);
      setCurrentPage(1);
      try { localStorage.setItem('uv_usersPerPage', String(size)); } catch {}
    } else {
      setPageSizeInput(String(usersPerPage));
    }
  };
  const [manageTarget, setManageTarget] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, suspended: 0, newThisMonth: 0, pendingVerification: 0, pendingReferral: 0 });

  // ── Pending Approvals filter — persists across reloads ─────────────────────
  const [showPendingOnly, setShowPendingOnly] = useState(() => {
    try { return localStorage.getItem('uv_pendingFilter') === 'true'; }
    catch { return false; }
  });
  const togglePendingFilter = () => {
    setShowPendingOnly(prev => {
      const next = !prev;
      try { localStorage.setItem('uv_pendingFilter', String(next)); } catch {}
      setCurrentPage(1);
      return next;
    });
  };

  // ── Referral commission period ─────────────────────────────────────────────
  // Stores the period after which a referrer can no longer earn commission.
  // Persisted to localStorage so it survives page reloads.
  const [refPeriod, setRefPeriod] = useState(() => {
    try { return JSON.parse(localStorage.getItem('refCommissionPeriod') || 'null') || { value: 3, unit: 'months' }; }
    catch { return { value: 3, unit: 'months' }; }
  });
  const [editingPeriod, setEditingPeriod] = useState(false);
  const [periodDraft,   setPeriodDraft]   = useState({ value: refPeriod.value, unit: refPeriod.unit });

  // Convert the period to milliseconds for comparison
  const refPeriodMs = (() => {
    const ms = { days: 864e5, weeks: 6048e5, months: 2592e6 };
    return refPeriod.value * (ms[refPeriod.unit] || ms.months);
  })();

  // Returns true if the referral is still within the commission window
  const isRefWithinPeriod = (user) => {
    if (!user.signed_via_referral) return false;
    const joinDate = user.date_joined ? new Date(user.date_joined) : null;
    if (!joinDate) return true; // no date → assume active
    return (Date.now() - joinDate.getTime()) < refPeriodMs;
  };

  const saveRefPeriod = () => {
    const n = parseInt(periodDraft.value, 10);
    if (isNaN(n) || n < 1) return;
    const next = { value: n, unit: periodDraft.unit };
    setRefPeriod(next);
    try { localStorage.setItem('refCommissionPeriod', JSON.stringify(next)); } catch {}
    setEditingPeriod(false);
  };
  const [newUserData, setNewUserData] = useState({
    email: '', phone: '', first_name: '', last_name: '', username: '', password: '', is_staff: false,
  });

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { calculateStats(); }, [users]);
  useEffect(() => {
    const h = () => setActiveDropdown(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const fetchedUsers = await apiService.fetchUsers(token);
      try { setAds(await apiService.fetchAds()); } catch { setAds([]); }
      setUsers(fetchedUsers);
    } catch (e) { console.error('❌ Error fetching users:', e); }
    finally { setLoading(false); }
  };

  const calculateStats = () => {
    const now = new Date();
    const ago30 = new Date(now.setDate(now.getDate() - 30));
    const reg = users.filter(u => !u.is_staff && !u.is_superuser);
    setStats({
      total:               reg.length,
      active:              reg.filter(u =>  u.is_active).length,
      suspended:           reg.filter(u => !u.is_active).length,
      newThisMonth:        reg.filter(u => new Date(u.date_joined) >= ago30).length,
      pendingVerification: reg.filter(u =>
        u.verification_status === 'pending' ||
        u.verification_status === 'pending_reverification' ||
        u.verification_status === 'reverify'
      ).length,
      pendingReferral: reg.filter(u => u.referral_mpesa && !u.referral_allowed).length,
    });
  };

  const getPhoneNumber = (user) => {
    if (user.phone_number || user.phone) return user.phone_number || user.phone;
    const ad = ads.find(a => {
      const em = (user.email || '').toLowerCase(), un = (user.username || '').toLowerCase();
      const ac = (a.contact || '').toLowerCase(), ap = (a.posted_by_email || '').toLowerCase(), au = (a.user || '').toLowerCase();
      return ac === em || ap === em || au === em || au === un;
    });
    if (ad?.contact?.match(/^\+?\d+$/) || ad?.contact?.match(/^0\d+$/)) return ad.contact;
    return null;
  };

  const getLocation = (user) => {
    if (user.location || user.city || user.town) return user.location || user.city || user.town;
    const ad = ads.find(a => {
      const em = (user.email || '').toLowerCase(), un = (user.username || '').toLowerCase();
      const ap = (a.posted_by_email || '').toLowerCase(), au = (a.user || '').toLowerCase();
      return ap === em || au === em || au === un;
    });
    return ad ? (ad.city || ad.location || null) : null;
  };

  const getSignupType = (user) => {
    // ✅ Priority order — most reliable signals first.

    // 1. Referred users get 'referral' type regardless of HOW they signed up
    //    (they could have used email/password or Google via a referral link).
    //    Show 'Referral' as the primary badge since it's the most specific info.
    if (user.is_referred) return 'referral';

    // 2. Explicit auth_provider field set by google/facebook_callback on UserProfile.
    const provider = (user.auth_provider || user.social_auth_provider || '').toLowerCase();
    if (provider === 'google')   return 'google';
    if (provider === 'facebook') return 'facebook';
    if (provider === 'phone')    return 'phone';
    if (provider && provider !== 'email') return provider;

    // 3. has_usable_password=false means OAuth signup (no password ever set).
    const email = (user.email || '').toLowerCase();
    if (user.has_usable_password === false) {
      if (email.includes('@gmail.com') || email.includes('@googlemail.com')) return 'google';
      if (email.startsWith('facebook_')) return 'facebook';
      return 'google';
    }

    // 4. Legacy heuristics for users created before auth_provider field existed.
    const un = (user.username || '').toLowerCase();
    if (user.is_google_user   || un.includes('google'))   return 'google';
    if (user.is_facebook_user || un.includes('facebook')) return 'facebook';
    if (user.socialaccount_set?.length > 0) {
      const p = (user.socialaccount_set[0]?.provider || '').toLowerCase();
      if (p) return p;
    }
    if (!email && getPhoneNumber(user)) return 'phone';

    return 'direct';
  };

  const SignupTypeBadge = ({ user }) => {
    const type = getSignupType(user).toLowerCase();
    const cfg = {
      google:   { label: 'Google',   bg: '#fef3c7', color: '#d97706', icon: '🔵' },
      facebook: { label: 'Facebook', bg: '#eff6ff', color: '#2563eb', icon: '📘' },
      phone:    { label: 'Phone',    bg: '#f0fdf4', color: '#16a34a', icon: '📱' },
      direct:   { label: 'Direct',   bg: '#f5f3ff', color: '#7c3aed', icon: '✉️'  },
      referral: { label: 'Referral', bg: '#ecfdf5', color: '#059669', icon: '🔗'  },
    };
    const c = cfg[type] || cfg.direct;
    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: c.bg, color: c.color, border: `1px solid ${c.color}33` }}>
          {c.icon} {c.label}
        </span>
        {/* Show who referred them as a subtle sub-line */}
        {type === 'referral' && user.referred_by && (
          <span style={{ fontSize: 10, color: '#94a3b8', paddingLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }} title={`Referred by ${user.referred_by}`}>
            by {user.referrer_name || user.referred_by}
          </span>
        )}
      </div>
    );
  };

  const getVerificationStatus = (user) => {
    if (user.verification_status) {
      return user.verification_status === 'reverify' ? 'pending_reverification' : user.verification_status;
    }
    if (user.is_email_verified !== undefined) {
      if (user.is_email_verified && (user.is_phone_verified || !getPhoneNumber(user))) return 'verified';
      if (user.is_email_verified) return 'partial';
      return 'unverified';
    }
    return 'unverified';
  };

  const VerificationBadge = ({ user }) => {
    const status = getVerificationStatus(user);
    const cfg = {
      verified:              { label: '✅ Verified',    bg: '#f0fdf4', color: '#16a34a', border: '#16a34a33' },
      partial:               { label: '~ Partial',      bg: '#fffbeb', color: '#d97706', border: '#d9780633' },
      pending:               { label: '⏳ Under Review',bg: '#eff6ff', color: '#2563eb', border: '#2563eb33' },
      pending_reverification:{ label: '🔄 Re-verify',   bg: '#fff7ed', color: '#c2410c', border: '#c2410c33' },
      unverified:            { label: '✗ Not Verified', bg: '#fef2f2', color: '#dc2626', border: '#dc262633' },
      rejected:              { label: '🚫 Rejected',    bg: '#fdf2f8', color: '#9333ea', border: '#9333ea33' },
    };
    const c = cfg[status] || cfg.unverified;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
        {c.label}
      </span>
    );
  };

  const openManage = (user) => { setManageTarget(user); setActiveDropdown(null); };

  const handleSaveEdit = async (userId, formData) => {
    const token = getToken();
    const res = await apiService.updateUser(userId, formData, token);
    if (res?.success) {
      // Optimistically update the table row so columns like ALLOWED TO REFER
      // reflect the new values immediately without waiting for fetchUsers().
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, ...formData } : u
      ));
      setManageTarget(prev =>
        prev && prev.id === userId ? { ...prev, ...formData } : prev
      );
      // ✅ Delay background sync slightly so the optimistic update renders
      // first — prevents a race where fetchUsers() overwrites the UI update
      // before the user sees the change (e.g. referral_allowed toggle).
      setTimeout(() => fetchUsers(), 800);
    } else throw new Error(res?.error || 'Failed to save');
  };

  // ✅ UPDATED: handles rejection → restore snapshot flow
  const handleSaveVerification = async (userId, verdict, userObj) => {
    // ── Rejection: confirm with snapshot details before proceeding ────────
    if (verdict === 'rejected') {
      const snap = loadSnapshot(userId, userObj);
      const restoredName = snap
        ? [snap.first_name, snap.last_name].filter(Boolean).join(' ') || '—'
        : null;
      const confirmMsg = snap
        ? `Reject changes and restore this account to its last verified state?\n\n` +
          `Profile will revert to:\n` +
          `  • Name:     ${restoredName}\n` +
          `  • Email:    ${snap.email     || '—'}\n` +
          `  • Phone:    ${snap.phone     || '—'}\n` +
          `  • Location: ${snap.location  || '—'}\n\n` +
          `Status will be restored to ✅ Verified.`
        : `No previous snapshot found.\n\n` +
          `Rejecting will mark the account as Unverified since there is nothing to restore.\n\nContinue?`;
      if (!window.confirm(confirmMsg)) return;
    }

    const token = getToken();
    const res = await apiService.adminSetUserVerification(userId, verdict, token);
    if (!res.success) throw new Error(res.error || 'Failed to save verification status');

    // ── Effective status — backend may override (e.g. rejected → verified) ─
    const effectiveStatus = res.verification_status || verdict;

    // ── Build merged update: include restored profile fields if present ───
    const updatedFields = {
      verification_status: effectiveStatus,
      ...(res.verified_snapshot ? { verified_snapshot: res.verified_snapshot } : {}),
      ...(res.restored_profile  ? {
        first_name:    res.restored_profile.first_name    || '',
        last_name:     res.restored_profile.last_name     || '',
        email:         res.restored_profile.email         || '',
        phone_number:  res.restored_profile.phone         || '',
        location:      res.restored_profile.location      || '',
        business_name: res.restored_profile.business_name || '',
      } : {}),
    };

    // ── Optimistic update — table row + modal reflect new state instantly ─
    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, ...updatedFields } : u
    ));
    setManageTarget(prev =>
      prev && prev.id === userId ? { ...prev, ...updatedFields } : prev
    );

    // Background refresh to sync any remaining server-side changes
    fetchUsers();
  };

  const isPendingApproval = (user) =>
    user.verification_status === 'pending' ||
    user.verification_status === 'pending_reverification' ||
    user.verification_status === 'reverify' ||
    !!(user.referral_mpesa && !user.referral_allowed);

  const filteredUsers = users
    .filter(user => {
      const isNotAdmin = !user.is_staff && !user.is_superuser;
      const phone = getPhoneNumber(user), location = getLocation(user);
      const q = searchTerm.toLowerCase();
      const ms = !q || user.username?.toLowerCase().includes(q) || user.email?.toLowerCase().includes(q) ||
                 phone?.toLowerCase().includes(q) || location?.toLowerCase().includes(q) ||
                 user.first_name?.toLowerCase().includes(q) || user.last_name?.toLowerCase().includes(q);
      const mr = roleFilter === 'all' || roleFilter === 'seller' || roleFilter === 'buyer';
      const mst = statusFilter === 'all' ||
                 (statusFilter === 'active'    &&  user.is_active) ||
                 (statusFilter === 'suspended' && !user.is_active) ||
                 (statusFilter === 'pending'   && (
                   user.verification_status === 'pending' ||
                   user.verification_status === 'pending_reverification' ||
                   user.verification_status === 'reverify'
                 ));
      const mpf = !showPendingOnly || isPendingApproval(user);
      return isNotAdmin && ms && mr && mst && mpf;
    })
    .sort((a, b) => {
      if (showPendingOnly) return 0;
      const aPending = isPendingApproval(a);
      const bPending = isPendingApproval(b);
      if (aPending && !bPending) return -1;
      if (!aPending && bPending) return 1;
      return 0;
    });

  const lastIdx      = currentPage * usersPerPage;
  const firstIdx     = lastIdx - usersPerPage;
  const currentUsers = filteredUsers.slice(firstIdx, lastIdx);
  const totalPages   = Math.ceil(filteredUsers.length / usersPerPage);

  const handleSelectAll  = e => setSelectedUsers(e.target.checked ? currentUsers.map(u => u.id) : []);
  const handleSelectUser = id => setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const res = await apiService.createUser(newUserData, getToken());
      if (res.success) {
        alert('User created successfully!');
        setShowAddUserModal(false);
        setNewUserData({ email: '', phone: '', first_name: '', last_name: '', username: '', password: '', is_staff: false });
        fetchUsers();
      } else alert(res.error || 'Failed to create user');
    } catch { alert('Failed to create user'); }
  };

  const handleToggleStatus = async (user) => {
    const action = user.is_active ? 'suspend' : 'activate';
    try {
      const res = await apiService.toggleUserStatus(user.id, getToken());
      if (res.success) fetchUsers();
      else alert(res.error || `Failed to ${action} user`);
    } catch { console.error(`Error ${action}ing user`); }
  };

  const handleDeleteUser = async (user) => {
    try {
      const res = await apiService.deleteUser(user.id, getToken());
      if (res.success) fetchUsers();
      else alert(res.error || 'Failed to delete user');
    } catch { console.error('Error deleting user'); }
  };

  const formatDate = d => !d ? 'N/A' :
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getInitials = user => {
    if (user.first_name && user.last_name) return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    return (user.username || 'U').substring(0, 2).toUpperCase();
  };

  if (loading) return (
    <div className="uv-wrapper">
      <div className="uv-loading"><div className="uv-spinner"/><p>Loading users...</p></div>
    </div>
  );

  return (
    <div className="uv-wrapper">

      <div className="uv-header">
        <div>
          <h2 className="uv-title">👥 Users Management</h2>
          <p className="uv-subtitle">Manage all registered users. Pending verifications are highlighted.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* ── Pending Verifications badge ── */}
          {stats.pendingVerification > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 99, background: '#eff6ff', color: '#2563eb', border: '1.5px solid #93c5fd', fontSize: 12, fontWeight: 700 }}>
              ⏳ {stats.pendingVerification} pending verification
            </div>
          )}
          {/* ── Pending Referral Approvals badge ── */}
          {stats.pendingReferral > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 99, background: '#fffbeb', color: '#d97706', border: '1.5px solid #fcd34d', fontSize: 12, fontWeight: 700 }}>
              🔗 {stats.pendingReferral} referral{stats.pendingReferral > 1 ? 's' : ''} awaiting approval
            </div>
          )}
          {/* ── Pending Approvals filter toggle ── */}
          {(stats.pendingVerification > 0 || stats.pendingReferral > 0) && (
            showPendingOnly ? (
              <button
                onClick={togglePendingFilter}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                  background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(220,38,38,.25)',
                }}
              >
                ✕ Clear Pending Filter
              </button>
            ) : (
              <button
                onClick={togglePendingFilter}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                  background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff',
                  border: 'none', cursor: 'pointer', boxShadow: '0 2px 8px rgba(245,158,11,.3)',
                }}
              >
                🔔 Show Pending Approvals
              </button>
            )
          )}
          <button className="uv-btn-add" onClick={() => setShowAddUserModal(true)}>+ Add User</button>
        </div>
      </div>

      <div className="uv-stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {[
          { label: 'Total Users',   value: stats.total,              cls: 'purple' },
          { label: 'Active',        value: stats.active,             cls: 'green'  },
          { label: 'Suspended',     value: stats.suspended,          cls: 'red'    },
          { label: 'New (30 days)', value: `+${stats.newThisMonth}`, cls: 'blue'   },
        ].map(s => (
          <div key={s.label} className={`uv-stat-card ${s.cls}`}>
            <div className="uv-stat-value">{s.value}</div>
            <div className="uv-stat-label">{s.label}</div>
          </div>
        ))}

        {/* ── Referral Commission Period Card ── */}
        <div className="uv-stat-card" style={{ borderLeft: '4px solid #059669', position: 'relative', minWidth: 0 }}>
          {!editingPeriod ? (
            <>
              <div className="uv-stat-value" style={{ color: '#059669', fontSize: 22 }}>
                {refPeriod.value} <span style={{ fontSize: 13, fontWeight: 600 }}>{refPeriod.unit}</span>
              </div>
              <div className="uv-stat-label">Referral Commission Period</div>
              <button
                onClick={() => { setPeriodDraft({ value: refPeriod.value, unit: refPeriod.unit }); setEditingPeriod(true); }}
                style={{
                  marginTop: 8, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                  border: '1.5px solid #6ee7b7', borderRadius: 99,
                  background: '#ecfdf5', color: '#059669', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >✏️ Edit</button>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                Commission Window
              </div>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <input
                  type="number" min="1" max="999"
                  value={periodDraft.value}
                  onChange={e => setPeriodDraft(d => ({ ...d, value: e.target.value }))}
                  style={{
                    width: 52, height: 30, padding: '0 6px', fontSize: 13, fontWeight: 700,
                    border: '1.5px solid #6ee7b7', borderRadius: 7, textAlign: 'center', outline: 'none',
                  }}
                />
                <select
                  value={periodDraft.unit}
                  onChange={e => setPeriodDraft(d => ({ ...d, unit: e.target.value }))}
                  style={{
                    height: 30, padding: '0 6px', fontSize: 12, fontWeight: 600,
                    border: '1.5px solid #6ee7b7', borderRadius: 7, background: '#fff',
                    cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 5 }}>
                <button onClick={saveRefPeriod} style={{ flex: 1, padding: '4px 0', fontSize: 11, fontWeight: 700, border: 'none', borderRadius: 7, background: '#059669', color: '#fff', cursor: 'pointer' }}>Save</button>
                <button onClick={() => setEditingPeriod(false)} style={{ flex: 1, padding: '4px 0', fontSize: 11, fontWeight: 600, border: '1.5px solid #e2e8f0', borderRadius: 7, background: '#fff', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.4 }}>
                After this window referrers earn no commission.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="uv-filters">
        <input type="text" className="uv-search" placeholder="Search by name, email, phone or location..."
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        <select className="uv-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="all">All Roles</option>
          <option value="seller">Seller</option>
          <option value="buyer">Buyer</option>
        </select>
        <select className="uv-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="pending">⏳ Pending Verification</option>
        </select>
        {showPendingOnly && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: '#fffbeb', border: '1.5px solid #fcd34d', fontSize: 13, fontWeight: 700, color: '#d97706', whiteSpace: 'nowrap' }}>
            🔔 Pending Approvals
            <button onClick={togglePendingFilter} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: '#d97706', lineHeight: 1, padding: '0 0 0 4px', fontWeight: 900 }} title="Clear filter">✕</button>
          </div>
        )}
      </div>

      <div className="uv-table-container">
        <table className="uv-table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={selectedUsers.length === currentUsers.length && currentUsers.length > 0} onChange={handleSelectAll}/></th>
              <th>USER</th><th>EMAIL</th><th>PHONE</th><th>LOCATION</th>
              <th>SIGNUP</th><th>REFEREE</th><th>REF LINK</th><th>VIA REF?</th><th>CODE USED</th><th>REFERRAL MPESA</th><th>STATUS</th><th>LISTINGS</th>
              <th>RATING</th><th>VERIFICATION</th><th>ALLOWED TO REFER</th><th>ACC EVER SUSPENDED</th><th>REASON FOR SUS</th><th>JOINED</th><th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length === 0 ? (
              <tr><td colSpan="20" className="uv-empty">No users found.</td></tr>
            ) : currentUsers.map((user, i) => {
              const phone = getPhoneNumber(user), location = getLocation(user);
              const normStatus = user.verification_status === 'reverify'
                ? 'pending_reverification' : (user.verification_status || 'unverified');
              const isPending  = normStatus === 'pending';
              const isReverif  = normStatus === 'pending_reverification';
              const rowChecks  = [user.first_name, user.last_name, user.email, phone, location, user.username, user.id_front, user.id_back];
              const rowComplete = rowChecks.every(Boolean);
              const isReadyForVerification = rowComplete && normStatus !== 'verified';

              const snap = loadSnapshot(user.id, user);
              let tableChangedCount = 0;
              if (snap && isReverif) {
                const cur = {
                  first_name: user.first_name || '', last_name: user.last_name || '',
                  email: user.email || '', phone: getPhoneNumber(user) || '',
                  location: getLocation(user) || '', username: user.username || '',
                  business_name: user.business_name || '',
                  has_id_front: !!user.id_front, has_id_back: !!user.id_back,
                };
                const normT = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
                Object.entries(cur).forEach(([k, v]) => {
                  const sv = snap[k];
                  if (typeof v === 'boolean') { if (v !== (sv === true || sv === 'true')) tableChangedCount++; }
                  else { if (normT(v) !== normT(sv)) tableChangedCount++; }
                });
              }

              // Green highlight when user has submitted a referral_mpesa number but
              // referral_allowed is still false — prompts admin to review & approve.
              const hasPendingReferral = !!(user.referral_mpesa && !user.referral_allowed);
              const rowBg = isReverif ? '#fff7ed' : isPending ? '#eff6ff' : hasPendingReferral ? '#f0fdf4' : isReadyForVerification ? '#fefce8' : undefined;

              return (
                <tr key={user.id}
                  className={`${selectedUsers.includes(user.id) ? 'uv-row-selected' : ''} ${i % 2 !== 0 && !rowBg ? 'uv-row-alt' : ''}`}
                  style={rowBg ? { background: rowBg } : {}}>
                  <td><input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => handleSelectUser(user.id)}/></td>
                  <td>
                    <div className="uv-user-cell">
                      <div className="uv-avatar" style={isReverif ? { background: 'linear-gradient(135deg,#ea580c,#f97316)' } : isPending ? { background: 'linear-gradient(135deg,#2563eb,#3b82f6)' } : {}}>
                        {getInitials(user)}
                      </div>
                      <div>
                        <div className="uv-name">{user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : user.username}</div>
                        <div className="uv-username">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="uv-cell-muted">{user.email || '—'}</td>
                  <td>{phone ? <span className="uv-pill-gray">📱 {phone}</span> : <span className="uv-dash">—</span>}</td>
                  <td>{location ? <span className="uv-pill-gray">📍 {location}</span> : <span className="uv-dash">—</span>}</td>
                  <td><SignupTypeBadge user={user}/></td>

                  {/* REFEREE — who referred this user */}
                  <td>
                    {user.is_referred && user.referred_by ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#059669', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }} title={user.referred_by}>
                          🔗 {user.referrer_name || user.referred_by}
                        </span>
                        {user.referrer_name && (
                          <span style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }} title={user.referred_by}>
                            {user.referred_by}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="uv-dash">—</span>
                    )}
                  </td>

                  {/* REF LINK — the referral link used to sign up */}
                  <td>
                    {user.referral_link ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontSize: 11, color: '#6366f1', fontFamily: 'monospace', background: '#eef2ff', padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130, display: 'inline-block' }} title={user.referral_link}>
                          ?ref={user.referral_code}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(user.referral_link);
                          }}
                          title={`Copy: ${user.referral_link}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: 2, color: '#94a3b8', flexShrink: 0 }}
                        >📋</button>
                      </div>
                    ) : (
                      <span className="uv-dash">—</span>
                    )}
                  </td>

                  {/* VIA REF? — signed_via_referral boolean; shows Yes-Used when commission window expired */}
                  <td style={{ textAlign: 'center' }}>
                    {user.signed_via_referral
                      ? isRefWithinPeriod(user)
                        ? <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#ecfdf5', color: '#059669', border: '1px solid #6ee7b7', whiteSpace: 'nowrap' }}>✅ Yes</span>
                        : <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74', whiteSpace: 'nowrap' }} title="Commission window expired — referrer no longer earns commission">⏱ Yes-Used</span>
                      : <span style={{ fontSize: 12, color: '#94a3b8', padding: '2px 8px', borderRadius: 99, background: '#f8fafc', border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>— No</span>
                    }
                  </td>

                  {/* CODE USED — referral_code_used string */}
                  <td style={{ textAlign: 'center' }}>
                    {user.referral_code_used
                      ? <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', padding: '2px 8px', borderRadius: 6, background: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                          {user.referral_code_used}
                        </span>
                      : <span className="uv-dash">—</span>
                    }
                  </td>

                  {/* REFERRAL MPESA — mpesa number submitted at signup for referral/enrolment */}
                  <td style={{ textAlign: 'center' }}>
                    {user.referral_mpesa
                      ? <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', padding: '2px 8px', borderRadius: 6, background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                          📱 {user.referral_mpesa}
                        </span>
                      : <span className="uv-dash">—</span>
                    }
                  </td>

                  <td><span className={`uv-badge ${user.is_active ? 'uv-badge-active' : 'uv-badge-suspended'}`}>{user.is_active ? '● Active' : '● Suspended'}</span></td>
                  <td><span className="uv-count">{user.ads_count || 0}</span></td>
                  <td><StarRating rating={Number(user.average_rating || 0)} count={Number(user.rating_count || 0)} /></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <VerificationBadge user={user}/>
                      {isReverif && tableChangedCount > 0 && (
                        <span title={`${tableChangedCount} field${tableChangedCount > 1 ? 's' : ''} changed since last verification`} style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 99, background: '#fef3c7', color: '#92400e', border: '1px solid #fde047', whiteSpace: 'nowrap', flexShrink: 0, cursor: 'default' }}>
                          ✏️ {tableChangedCount} changed
                        </span>
                      )}
                      {isReadyForVerification && !isReverif && (
                        <span title='Profile 100% complete — ready for verification' style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047', whiteSpace: 'nowrap', flexShrink: 0 }}>● Ready</span>
                      )}
                      <button onClick={e => { e.stopPropagation(); openManage(user); }} title="Manage verification" style={{
                        width: 22, height: 22, borderRadius: '50%',
                        border: isReverif ? '1.5px solid #fdba74' : isPending ? '1.5px solid #93c5fd' : isReadyForVerification ? '1.5px solid #fde047' : '1.5px solid #d1d5db',
                        background: isReverif ? '#fff7ed' : isPending ? '#dbeafe' : isReadyForVerification ? '#fef9c3' : '#f8fafc',
                        cursor: 'pointer', fontSize: 11,
                        color: isReverif ? '#c2410c' : isPending ? '#2563eb' : isReadyForVerification ? '#854d0e' : '#6b7280',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>✎</button>
                    </div>
                  </td>
                  {/* ALLOWED TO REFER */}
                  <td style={{ textAlign: 'center' }}>
                    {user.referral_allowed
                      ? <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#ecfdf5', color: '#059669', border: '1px solid #6ee7b7', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>✅ Allowed</span>
                      : user.referral_mpesa
                        ? <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#fffbeb', color: '#d97706', border: '1px solid #fcd34d', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>⏳ Pending Approval</span>
                        : <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: '#f8fafc', color: '#94a3b8', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>🚫 Not Allowed</span>
                    }
                  </td>

                  {/* ACC EVER SUSPENDED — stays 'Yes' permanently once user was ever suspended */}
                  <td style={{ textAlign: 'center' }}>
                    {(user.ever_suspended || !user.is_active)
                      ? <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>🚨 Yes</span>
                      : <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>✅ No</span>
                    }
                  </td>

                  {/* REASON FOR SUS — truncated in cell, full text on click */}
                  <td style={{ maxWidth: 160 }}>
                    {user.suspension_reason ? (
                      <button
                        onClick={() => window.alert(`Suspension Reason\n\n${user.suspension_reason}`)}
                        title="Click to view full reason"
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          textAlign: 'left', width: '100%',
                        }}
                      >
                        <span style={{
                          display: 'block', fontSize: 12, color: '#dc2626',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          maxWidth: 150, textDecoration: 'underline dotted', textUnderlineOffset: 3,
                        }}>
                          {user.suspension_reason}
                        </span>
                      </button>
                    ) : (
                      <span className="uv-dash">—</span>
                    )}
                  </td>

                  <td className="uv-cell-muted uv-date">{formatDate(user.date_joined)}</td>
                  <td>
                    <UserActions
                      user={user}
                      onVerify={openManage}
                      onToggleStatus={handleToggleStatus}
                      onDelete={handleDeleteUser}
                      isOpen={activeDropdown === user.id}
                      onToggle={() => setActiveDropdown(activeDropdown === user.id ? null : user.id)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="uv-pagination">
          <span className="uv-page-info">Showing {firstIdx + 1}–{Math.min(lastIdx, filteredUsers.length)} of {filteredUsers.length}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div className="uv-page-controls">
              <button className="uv-page-btn" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>‹</button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i} className={`uv-page-btn ${currentPage === i + 1 ? 'uv-page-active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
              ))}
              <button className="uv-page-btn" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>›</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>Rows per page:</label>
              <input
                type="number" min="1" max="200"
                value={pageSizeInput}
                onChange={e => setPageSizeInput(e.target.value)}
                onBlur={handlePageSizeChange}
                onKeyDown={e => { if (e.key === 'Enter') handlePageSizeChange(); }}
                style={{ width: 60, height: 32, padding: '0 8px', border: '1.5px solid #6366f1', borderRadius: 7, fontSize: 13, fontWeight: 700, textAlign: 'center', outline: 'none', fontFamily: 'inherit' }}
              />
            </div>
          </div>
        </div>
      )}

      {manageTarget && (
        <UserManageModal
          user={manageTarget}
          onClose={() => setManageTarget(null)}
          onSaveEdit={handleSaveEdit}
          onSaveVerification={handleSaveVerification}
          getPhoneNumber={getPhoneNumber}
          getLocation={getLocation}
        />
      )}

      {showAddUserModal && (
        <div className="uv-modal-overlay" onClick={() => setShowAddUserModal(false)}>
          <div className="uv-modal-box" onClick={e => e.stopPropagation()}>
            <div className="uv-modal-header">
              <h3>Add New User</h3>
              <button className="uv-modal-close" onClick={() => setShowAddUserModal(false)}>×</button>
            </div>
            <form className="uv-modal-form" onSubmit={handleAddUser}>
              <div className="uv-form-row">
                <div className="uv-form-field"><label>First Name</label><input type="text" placeholder="John" value={newUserData.first_name} onChange={e => setNewUserData({ ...newUserData, first_name: e.target.value })}/></div>
                <div className="uv-form-field"><label>Last Name</label><input type="text" placeholder="Doe" value={newUserData.last_name} onChange={e => setNewUserData({ ...newUserData, last_name: e.target.value })}/></div>
              </div>
              <div className="uv-form-row">
                <div className="uv-form-field"><label>Email *</label><input type="email" required placeholder="john@example.com" value={newUserData.email} onChange={e => setNewUserData({ ...newUserData, email: e.target.value })}/></div>
                <div className="uv-form-field"><label>Phone *</label><input type="tel" required placeholder="+254712345678" value={newUserData.phone} onChange={e => setNewUserData({ ...newUserData, phone: e.target.value })}/></div>
              </div>
              <div className="uv-form-field"><label>Username</label><input type="text" placeholder="Auto-generated if blank" value={newUserData.username} onChange={e => setNewUserData({ ...newUserData, username: e.target.value })}/></div>
              <div className="uv-form-field"><label>Password</label><input type="password" placeholder="Auto-generated if blank" value={newUserData.password} onChange={e => setNewUserData({ ...newUserData, password: e.target.value })}/></div>
              <div className="uv-modal-actions">
                <button type="button" className="uv-btn-cancel" onClick={() => setShowAddUserModal(false)}>Cancel</button>
                <button type="submit" className="uv-btn-submit">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersView;